// @ts-nocheck
/**
 * This page contains content required for Phase 2 marketplace functionality. Sections are intentionally
 * preserved but hidden during Phase 1.
 */

import React from "react";
import LegalPageLayout, { BulletList, Section } from "../components/LegalPageLayout";

export default function IPDisclaimer() {
  return (
    <LegalPageLayout
      title="Intellectual Property & Trademark Disclaimer"
      subtitle="How Cardex references trademarks and third-party content."
      updated="February 7, 2026"
    >
      <Section title="Phase 1 Notice">
        <p style={{ 
          background: "linear-gradient(135deg, rgba(124, 243, 255, 0.1), rgba(111, 66, 193, 0.1))",
          padding: "14px 18px",
          borderRadius: "10px",
          border: "1px solid rgba(124, 243, 255, 0.25)",
          color: "rgba(124, 243, 255, 0.95)",
          fontWeight: "500"
        }}>
          This disclaimer reflects Cardex Phase 1 functionality. Cardex is a local, in-person trading 
          coordination platform. We do not facilitate payments, shipping, or online transactions.
        </p>
      </Section>

      <Section title="Platform Role">
        <p>
          Cardex is a <strong>trading coordination platform</strong> that helps collectors connect for 
          local, in-person trades. We do not manufacture, sell, authenticate, grade, or endorse any of 
          the items referenced or traded through our platform.
        </p>
        <p>
          All trades occur directly between users in person. Cardex does not facilitate online 
          transactions, payments, or shipping.
        </p>
      </Section>

      <Section title="Trading Card Trademarks">
        <p>
          All trading card names, images, logos, and trademarks referenced on this platform are the
          property of their respective owners, including but not limited to:
        </p>
        <BulletList
          items={[
            "Pokémon: The Pokémon Company International, Nintendo, Game Freak",
            "Magic: The Gathering: Wizards of the Coast LLC",
            "Yu-Gi-Oh!: Konami Digital Entertainment",
            "One Piece Card Game: Bandai Namco Entertainment",
            "Disney Lorcana: Ravensburger North America",
            "Flesh and Blood: Legend Story Studios",
            "Dragon Ball Super Card Game: Bandai",
            "And all other trading card game publishers"
          ]}
        />
        <p>
          Cardex is <strong>not affiliated with, endorsed by, or sponsored by</strong> any of these
          companies or trademark holders.
        </p>
      </Section>

      <Section title="Use of Trademarks & Images">
        <p>References to trading card names, set names, and card attributes are used solely for:</p>
        <BulletList
          items={[
            "Descriptive purposes (identifying cards for trading)",
            "Fair use under trademark law",
            "Facilitating in-person trading coordination"
          ]}
        />
        <p>
          These references do not imply any affiliation, endorsement, or sponsorship by the trademark
          owners.
        </p>
      </Section>

      <Section title="User-Generated Content">
        <p>
          All trade listings, photos, descriptions, and other content are created by independent users.
          Cardex does not verify the accuracy of:
        </p>
        <BulletList
          items={[
            "Card descriptions or condition assessments",
            "Rarity or grading claims",
            "Card authenticity",
            "Value estimates"
          ]}
        />
        <p>
          Users are responsible for inspecting cards during in-person meetups and making informed trading
          decisions.
        </p>
      </Section>

      <Section title="Third-Party Grading Services">
        <p>
          References to third-party grading companies (PSA, BGS, CGC, etc.) are for descriptive purposes
          only. Cardex is not affiliated with these companies.
        </p>
        <p>
          Users should verify grading certificate numbers directly with the grading company when trading 
          high-value cards.
        </p>
      </Section>

      <Section title="Counterfeit Protection">
        <p>Cardex prohibits posting counterfeit, altered, or inauthentic cards. However:</p>
        <BulletList
          items={[
            "We do not authenticate every card posted",
            "Users are responsible for ensuring cards are genuine",
            "Traders should inspect cards carefully during in-person meetups"
          ]}
        />
        <p>
          Posting counterfeit cards violates our Community Guidelines and may result in account 
          termination.
        </p>
      </Section>

      <Section title="Third-Party Links">
        <p>Cardex may reference third-party sites such as:</p>
        <BulletList
          items={[
            "TCG convention websites",
            "Card shop locations",
            "Grading company authentication tools"
          ]}
        />
        <p>These references are provided for user convenience. We are not responsible for:</p>
        <BulletList
          items={[
            "The accuracy of external website content",
            "Third-party policies or practices",
            "Services provided by external sites"
          ]}
        />
      </Section>

      <Section title="Copyright & DMCA">
        <p>
          Cardex respects the intellectual property rights of others. If you believe content on our
          platform infringes your copyright, please contact us with:
        </p>
        <BulletList
          items={[
            "A description of the copyrighted work",
            "The location of the infringing content (URL or post ID)",
            "Your contact information",
            "A statement of good faith belief that use is unauthorized",
            "A statement that the information is accurate and you are authorized to act",
            "Your physical or electronic signature"
          ]}
        />
        <p>Email DMCA notices to: <strong>legal@cardexhq.com</strong></p>
      </Section>

      <Section title="User Responsibilities">
        <p>When posting trade listings on Cardex, users represent that:</p>
        <BulletList
          items={[
            "They own the cards or have the right to trade them",
            "Their posts do not infringe any intellectual property rights",
            "Their use of product names and images complies with fair use and trademark law",
            "Cards are authentic and not counterfeit"
          ]}
        />
      </Section>

      <Section title="Limitation of Liability">
        <p>Cardex is not liable for:</p>
        <BulletList
          items={[
            "Counterfeit or inauthentic cards traded by users",
            "Trademark or copyright infringement by users",
            "Inaccurate card descriptions or misrepresentation",
            "Disputes between traders regarding authenticity or condition"
          ]}
        />
        <p>
          Users trade at their own risk. We recommend meeting in public places and inspecting all cards 
          before completing trades.
        </p>
      </Section>

      <Section title="Changes to This Disclaimer">
        <p>
          We may update this disclaimer as we add new features or respond to legal requirements. Check 
          this page periodically for updates.
        </p>
      </Section>

      <Section title="Contact Information">
        <p>For questions about intellectual property, trademarks, or copyright on Cardex:</p>
        <p><strong>legal@cardexhq.com</strong></p>
      </Section>

      <p className="legal-footer-text">
        This disclaimer clarifies Cardex's role as a trading coordination platform and our relationship 
        with trademark holders and grading services.
      </p>
    </LegalPageLayout>
  );
}
